import { v4 as uuidv4 } from 'uuid';

import { STORY_COPY } from 'copy/story';

import {
  ApiStoryItemFragment,
  ApiStoryGroupFragment,
} from 'gql/api/api-fragments.generated';
import { factory } from 'utils/factory';

import {
  StoryBlockquote,
  StoryGroup,
  StoryH1,
  StoryH2,
  StoryImage,
  StoryImageAst,
  StoryImageContentEmpty,
  StoryImageContentPopulated,
  StoryInventoryItem,
  StoryParagraph,
} from 'types/Story';

export const apiStoryGroupFactory = factory<ApiStoryGroupFragment>({
  id: uuidv4(),
  size: 'NONE',
  items: [],
});

export const apiStoryItemFactory = factory<ApiStoryItemFragment>({
  id: uuidv4(),
  type: 'PARAGRAPH',
  contentV2: {
    __typename: 'StoryItemJsonContent',
    ast: {},
  },
});

export const storyParagraphFactory = factory<StoryParagraph>({
  type: 'doc',
  content: [
    {
      type: 'paragraph',
      content: [
        {
          type: 'text',
          text: '',
        },
      ],
    },
  ],
});

export const STORY_PARAGRAPH_PLACEHOLDER = storyParagraphFactory.get();

export const storyH1Factory = factory<StoryH1>({
  type: 'doc',
  content: [
    {
      type: 'heading',
      attrs: {
        level: 1,
      },
      content: [
        {
          type: 'text',
          text: '',
        },
      ],
    },
  ],
});

export const STORY_H1_PLACEHOLDER = storyH1Factory.get();

export const storyH2Factory = factory<StoryH2>({
  type: 'doc',
  content: [
    {
      type: 'heading',
      attrs: {
        level: 2,
      },
      content: [
        {
          type: 'text',
          text: '',
        },
      ],
    },
  ],
});

export const STORY_H2_PLACEHOLDER = storyH2Factory.get();

export const storyBlockquoteFactory = factory<StoryBlockquote>({
  type: 'doc',
  content: [
    {
      type: 'blockquote',
      content: [
        {
          type: 'paragraph',
          content: [
            {
              type: 'text',
              text: '',
            },
          ],
        },
        {
          type: 'footer',
          content: [
            {
              type: 'cite',
              content: [
                {
                  type: 'text',
                  text: '',
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

export const STORY_BLOCKQUOTE_PLACEHOLDER = storyBlockquoteFactory.get();

const storyGroupFactory = factory<StoryGroup>({
  id: uuidv4(),
  size: 'NONE',
  items: [],
});

export const storyImageAstFactory = factory<StoryImageAst>({
  type: 'doc',
  content: [
    {
      type: 'figure',
      content: [
        {
          type: 'img',
          attr: {
            src: 'https://f8n-ipfs-production.imgix.net/QmeUDBWgScMdtV5YsauzyJ8a4Wg7sokCkxkhypL2r5K1Jf/nft.jpg?q=80&auto=format%2Ccompress&cs=srgb&w=3000&h=3000&fit=max',
          },
        },
        {
          type: 'figcaption',
          content: [
            {
              type: 'text',
              text: 'Caption',
            },
          ],
        },
      ],
    },
  ],
});

export const storyImageContentEmptyFactory = factory<StoryImageContentEmpty>({
  state: 'EMPTY',
  caption: '',
});

export const storyImageContentFactory = factory<StoryImageContentPopulated>({
  state: 'POPULATED',
  base64Preview: null,
  ast: storyImageAstFactory.get(),
});

export const storyImageFactory = factory<StoryImage>({
  id: uuidv4(),
  type: 'IMAGE',
  content: storyImageContentEmptyFactory.get(),
  placeholder: null,
});

export const storyInventoryItemFactory = factory<StoryInventoryItem>({
  id: uuidv4(),
  type: 'INVENTORY_ITEM',
  placeholder: null,
  content: {
    state: 'EMPTY',
  },
});

export const getInitialStory = (): StoryGroup[] => [
  storyGroupFactory.with({
    id: uuidv4(),
    items: [
      {
        id: uuidv4(),
        type: 'H1',
        content: STORY_H1_PLACEHOLDER,
        placeholder: STORY_COPY.INITIAL_PLACEHOLDER,
      },
    ],
  }),
  storyGroupFactory.with({
    id: uuidv4(),
    items: [
      {
        id: uuidv4(),
        type: 'IMAGE',
        content: storyImageContentEmptyFactory.get(),
        placeholder: null,
      },
    ],
  }),
];
